import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import React from 'react'
import {
  SELECTION_CHANGE_COMMAND,
  FORMAT_TEXT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  // $getNodeByKey,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_ELEMENT_COMMAND
} from 'lexical'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import {
  $setBlocksType,
  $isAtNodeEnd,
  $getSelectionStyleValueForProperty,
  $patchStyleText
} from '@lexical/selection'
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  INSERT_CHECK_LIST_COMMAND,
  $isListNode,
  ListNode
} from '@lexical/list'
import { createPortal } from 'react-dom'
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode
} from '@lexical/rich-text'
import { $createCodeNode, $isCodeNode } from '@lexical/code'
import { sanitizeUrl } from '../../utils/url'
import { setFloatingElemPositionForLinkEditor } from '../../utils/setFloatingElemPositionForLinkEditor'
import DropdownColorPicker from '../../ui/DropdownColorPicker'
import DropDown, { DropDownItem } from '../../ui/DropDown'
import { INSERT_COLLAPSIBLE_COMMAND } from '../CollapsiblePlugin'
import { INSERT_EMBED_COMMAND } from '@lexical/react/LexicalAutoEmbedPlugin'
import { InsertImageDialog } from '../ImagesPlugin'
import { InsertTableDialog } from '../TablePlugin'
import { EmbedConfigs } from '../AutoEmbedPlugin'
import useModal from '../../hooks/useModal'

const LowPriority = 1

const supportedBlockTypes = new Set([
  'paragraph',
  'quote',
  'check',
  'code',
  'h1',
  'h2',
  'bullet',
  'number'
])

const blockTypeToBlockName = {
  code: 'Code Block',
  h1: 'Large Heading',
  h2: 'Small Heading',
  h3: 'Heading',
  h4: 'Heading',
  h5: 'Heading',
  check: 'Check List',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
  bullet: 'Bulleted List'
}

function Divider () {
  return <div className='divider' />
}

function FloatingLinkEditor ({ editor, anchorElem }) {
  const editorRef = React.useRef(null)
  const inputRef = React.useRef(null)
  const [linkUrl, setLinkUrl] = React.useState('')
  const [editedLinkUrl, setEditedLinkUrl] = React.useState('')
  const [isEditMode, setEditMode] = React.useState(false)
  const [lastSelection, setLastSelection] = React.useState(null)

  const updateLinkEditor = React.useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection)
      const parent = node.getParent()
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL())
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL())
      } else {
        setLinkUrl('')
      }
    }
    const editorElem = editorRef.current
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (editorElem === null) {
      return
    }

    const rootElement = editor.getRootElement()

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect =
        nativeSelection.focusNode?.parentElement?.getBoundingClientRect()
      if (domRect) {
        domRect.y += 40
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem)
      }
      setLastSelection(selection)
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem)
      }
      setLastSelection(null)
      setEditMode(false)
      setLinkUrl('')
    }

    return true
  }, [anchorElem, editor])

  const handleEditedLinkSubmission = () => {
    if (lastSelection !== null) {
      if (editedLinkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(editedLinkUrl))
      }
      setEditMode(false)
    }
  }

  React.useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor()
          return true
        },
        LowPriority
      )
    )
  }, [editor, updateLinkEditor])

  React.useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor()
    })
  }, [editor, updateLinkEditor])

  React.useEffect(() => {
    if (isEditMode && inputRef.current) {
      setEditedLinkUrl(linkUrl)
      inputRef.current.focus()
    }
    //eslint-disable-next-line
  }, [isEditMode])

  return (
    <div ref={editorRef} className='link-editor'>
      {isEditMode ? (
        <>
          <input
            autoFocus
            ref={inputRef}
            className='link-input'
            value={editedLinkUrl}
            onChange={event => {
              setEditedLinkUrl(event.target.value)
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleEditedLinkSubmission()
              } else if (event.key === 'Escape') {
                event.stopPropagation()
                event.preventDefault()
                setEditedLinkUrl('')
                setEditMode(false)
              }
            }}
          />
          <div>
            <div
              className='link-cancel'
              role='button'
              tabIndex={0}
              onMouseDown={event => event.preventDefault()}
              onClick={() => {
                setEditedLinkUrl('')
                setEditMode(false)
              }}
            />

            <div
              className='link-confirm'
              role='button'
              tabIndex={0}
              onMouseDown={event => event.preventDefault()}
              onClick={handleEditedLinkSubmission}
            />
          </div>
        </>
      ) : (
        <div className='link-view'>
          <a
            href={sanitizeUrl(linkUrl)}
            target='_blank'
            rel='noopener noreferrer'
          >
            {linkUrl}
          </a>
          <div
            className='link-edit'
            role='button'
            tabIndex={0}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              setEditMode(true)
            }}
          />
          <div
            className='link-trash'
            role='button'
            tabIndex={0}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
            }}
          />
        </div>
      )}
    </div>
  )
}

// function Select ({ onChange, className, options, value }) {
//   return (
//     <select className={className} onChange={onChange} value={value}>
//       <option hidden={true} value='' />
//       {options.map(option => (
//         <option key={option} value={option}>
//           {option}
//         </option>
//       ))}
//     </select>
//   )
// }

function getSelectedNode (selection) {
  const anchor = selection.anchor
  const focus = selection.focus
  const anchorNode = selection.anchor.getNode()
  const focusNode = selection.focus.getNode()
  if (anchorNode === focusNode) {
    return anchorNode
  }
  const isBackward = selection.isBackward()
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode
  }
}

function BlockOptionsDropdownList ({
  editor,
  blockType,
  toolbarRef,
  setShowBlockOptionsDropDown
}) {
  const dropDownRef = React.useRef(null)

  React.useLayoutEffect(() => {
    const toolbar = toolbarRef.current
    const dropDown = dropDownRef.current

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect()
      dropDown.style.top = `${top + 40}px`
      dropDown.style.left = `${left}px`
    }
  }, [dropDownRef, toolbarRef])

  React.useEffect(() => {
    const dropDown = dropDownRef.current
    const toolbar = toolbarRef.current

    if (dropDown !== null && toolbar !== null) {
      const handle = event => {
        const target = event.target

        if (!dropDown.contains(target) && !toolbar.contains(target)) {
          setShowBlockOptionsDropDown(false)
        }
      }
      document.addEventListener('click', handle)

      return () => {
        document.removeEventListener('click', handle)
      }
    }
  }, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef])

  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode())
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatLargeHeading = () => {
    if (blockType !== 'h1') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode('h1'))
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatSmallHeading = () => {
    if (blockType !== 'h2') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode('h2'))
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND)
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND)
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND)
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode())
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createCodeNode())
        }
      })
    }
    setShowBlockOptionsDropDown(false)
  }

  return (
    <div className='dropdown' ref={dropDownRef}>
      <button className='item' onClick={formatParagraph}>
        <span className='icon paragraph' />
        <span className='text'>Normal</span>
        {blockType === 'paragraph' && <span className='active' />}
      </button>
      <button className='item' onClick={formatLargeHeading}>
        <span className='icon large-heading' />
        <span className='text'>Large Heading</span>
        {blockType === 'h1' && <span className='active' />}
      </button>
      <button className='item' onClick={formatSmallHeading}>
        <span className='icon small-heading' />
        <span className='text'>Small Heading</span>
        {blockType === 'h2' && <span className='active' />}
      </button>
      <button className='item' onClick={formatBulletList}>
        <span className='icon bullet-list' />
        <span className='text'>Bullet List</span>
        {blockType === 'ul' && <span className='active' />}
      </button>
      <button className='item' onClick={formatNumberedList}>
        <span className='icon numbered-list' />
        <span className='text'>Numbered List</span>
        {blockType === 'ol' && <span className='active' />}
      </button>
      <button className='item' onClick={formatCheckList}>
        <span className='icon check-list' />
        <span className='text'>Check List</span>
        {blockType === 'check' && <span className='active' />}
      </button>
      <button className='item' onClick={formatQuote}>
        <span className='icon quote' />
        <span className='text'>Quote</span>
        {blockType === 'quote' && <span className='active' />}
      </button>
      <button className='item' onClick={formatCode}>
        <span className='icon code' />
        <span className='text'>Code Block</span>
        {blockType === 'code' && <span className='active' />}
      </button>
    </div>
  )
}

export default function ToolbarPlugin ({
  anchorElem = document.body,
  showToolbar,
  disableImages = false,
  isSmallWidthViewport = false
}) {
  const [editor] = useLexicalComposerContext()
  const [activeEditor, setActiveEditor] = React.useState(editor)
  const toolbarRef = React.useRef(null)
  const [blockType, setBlockType] = React.useState('paragraph')
  // const [selectedElementKey, setSelectedElementKey] = React.useState(null)
  const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] =
    React.useState(false)
  // const [codeLanguage, setCodeLanguage] = useState('')
  const [isLink, setIsLink] = React.useState(false)
  const [isBold, setIsBold] = React.useState(false)
  const [isItalic, setIsItalic] = React.useState(false)
  const [isUnderline, setIsUnderline] = React.useState(false)
  const [isStrikethrough, setIsStrikethrough] = React.useState(false)
  // const [isCode, setIsCode] = useState(false)
  const [modal, showModal] = useModal()
  const [isEditable, setIsEditable] = React.useState(() => editor.isEditable())
  const [fontColor, setFontColor] = React.useState('#000')

  const updateToolbar = React.useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow()
      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)
      if (elementDOM !== null) {
        // setSelectedElementKey(elementKey)
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode)
          const type = parentList
            ? parentList.getListType()
            : element.getListType()
          setBlockType(type)
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType()
          setBlockType(type)
          if ($isCodeNode(element)) {
            // setCodeLanguage(element.getLanguage() || getDefaultCodeLanguage())
          }
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      // setIsCode(selection.hasFormat('code'))

      // Update links
      const node = getSelectedNode(selection)
      const parent = node.getParent()

      if (
        ($isLinkNode(parent) || $isLinkNode(node)) &&
        // check if the selection is not at the end of the node, to avoid showing the link editor when the user is typing or when caret is at the end of the link
        !$isAtNodeEnd(selection.anchor)
      ) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }
      setFontColor(
        $getSelectionStyleValueForProperty(selection, 'color', '#000')
      )
    }
  }, [editor])

  React.useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener(editable => {
        setIsEditable(editable)
      }),
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar()
          return false
        },
        LowPriority
      )
    )
  }, [editor, updateToolbar])

  React.useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        updateToolbar()
        setActiveEditor(newEditor)
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, updateToolbar])

  // const codeLanguges = useMemo(() => getCodeLanguages(), [])
  // const onCodeLanguageSelect = useCallback(
  //   e => {
  //     editor.update(() => {
  //       if (selectedElementKey !== null) {
  //         const node = $getNodeByKey(selectedElementKey)
  //         if ($isCodeNode(node)) {
  //           node.setLanguage(e.target.value)
  //         }
  //       }
  //     })
  //   },
  //   [editor, selectedElementKey]
  // )

  const applyStyleText = React.useCallback(
    styles => {
      activeEditor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, styles)
        }
      })
    },
    [activeEditor]
  )

  const onFontColorSelect = React.useCallback(
    value => {
      applyStyleText({ color: value })
    },
    [applyStyleText]
  )

  const insertLink = React.useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://')
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink])

  return (
    <>
      {showToolbar && (
        <div className='toolbar' ref={toolbarRef}>
          {supportedBlockTypes.has(blockType) && (
            <>
              <button
                className='toolbar-item block-controls'
                onClick={() =>
                  setShowBlockOptionsDropDown(!showBlockOptionsDropDown)
                }
                aria-label='Formatting Options'
              >
                <span className={'icon block-type ' + blockType} />
                <span className='text dropdown-button-text'>
                  {blockTypeToBlockName[blockType]}
                </span>
                <i className='chevron-down' />
              </button>
              {showBlockOptionsDropDown &&
                createPortal(
                  <BlockOptionsDropdownList
                    editor={editor}
                    blockType={blockType}
                    toolbarRef={toolbarRef}
                    setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
                  />,
                  document.body
                )}
              <Divider />
            </>
          )}
          <>
            <button
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
              }}
              className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
              aria-label='Format Bold'
            >
              <i className='format bold' />
            </button>
            <button
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
              }}
              className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
              aria-label='Format Italics'
            >
              <i className='format italic' />
            </button>
            <button
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
              }}
              className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
              aria-label='Format Underline'
            >
              <i className='format underline' />
            </button>
            <button
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
              }}
              className={
                'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')
              }
              aria-label='Format Strikethrough'
            >
              <i className='format strikethrough' />
            </button>
            <button
              onClick={insertLink}
              className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
              aria-label='Insert Link'
            >
              <i className='format link' />
            </button>
            <DropdownColorPicker
              disabled={!isEditable}
              buttonClassName='toolbar-item color-picker'
              buttonAriaLabel='Formatting text color'
              buttonIconClassName='icon font-color'
              color={fontColor}
              onChange={onFontColorSelect}
              title='text color'
            />
            <DropDown
              disabled={!isEditable}
              buttonLabel='Align'
              buttonIconClassName='icon left-align'
              buttonClassName='toolbar-item spaced alignment'
              buttonAriaLabel='Formatting options for text alignment'
            >
              <DropDownItem
                onClick={() => {
                  activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')
                }}
                className='item'
              >
                <i className='icon left-align' />
                <span className='text '>Left Align</span>
              </DropDownItem>
              <DropDownItem
                onClick={() => {
                  activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')
                }}
                className='item'
              >
                <i className='icon center-align' />
                <span className='text'>Center Align</span>
              </DropDownItem>
              <DropDownItem
                onClick={() => {
                  activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')
                }}
                className='item'
              >
                <i className='icon right-align' />
                <span className='text'>Right Align</span>
              </DropDownItem>
              <DropDownItem
                onClick={() => {
                  activeEditor.dispatchCommand(
                    FORMAT_ELEMENT_COMMAND,
                    'justify'
                  )
                }}
                className='item'
              >
                <i className='icon justify-align' />
                <span className='text'>Justify Align</span>
              </DropDownItem>
            </DropDown>
            <DropDown
              disabled={!isEditable}
              buttonClassName='toolbar-item spaced'
              buttonLabel='Insert'
              buttonAriaLabel='Insert specialized editor node'
              buttonIconClassName='icon plus'
            >
              {!disableImages && (
                <DropDownItem
                  onClick={() => {
                    showModal('Insert Image', onClose => (
                      <InsertImageDialog
                        activeEditor={activeEditor}
                        onClose={onClose}
                      />
                    ))
                  }}
                  className='item'
                >
                  <i className='icon image' />
                  <span className='text'>Image</span>
                </DropDownItem>
              )}
              <DropDownItem
                onClick={() => {
                  editor.dispatchCommand(INSERT_COLLAPSIBLE_COMMAND, undefined)
                }}
                className='item'
              >
                <i className='icon caret-right' />
                <span className='text'>Collapsible container</span>
              </DropDownItem>
              <DropDownItem
                onClick={() => {
                  showModal('Insert Table', onClose => (
                    <InsertTableDialog
                      activeEditor={activeEditor}
                      onClose={onClose}
                    />
                  ))
                }}
                className='item'
              >
                <i className='icon table' />
                <span className='text'>Table</span>
              </DropDownItem>
              {EmbedConfigs.map(embedConfig => (
                <DropDownItem
                  key={embedConfig.type}
                  onClick={() => {
                    activeEditor.dispatchCommand(
                      INSERT_EMBED_COMMAND,
                      embedConfig.type
                    )
                  }}
                  className='item'
                >
                  {embedConfig.icon}
                  <span className='text'>{embedConfig.contentName}</span>
                </DropDownItem>
              ))}
            </DropDown>
          </>
        </div>
      )}
      {isLink &&
        !isSmallWidthViewport &&
        createPortal(
          <FloatingLinkEditor editor={editor} anchorElem={anchorElem} />,
          anchorElem
        )}
      {modal}
    </>
  )
}
